import React from "react";

export function PageHeader({ eyebrow, title, description, actions }) {
  return (
    <div
      className="flex flex-col gap-4 border-b border-slate-200 bg-white px-8 py-6 md:flex-row md:items-end md:justify-between"
      data-testid="page-header"
    >
      <div>
        {eyebrow && (
          <div className="overline mb-2 text-[var(--descon-blue)]" data-testid="page-eyebrow">
            {eyebrow}
          </div>
        )}
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900" data-testid="page-title">
          {title}
        </h1>
        {description && (
          <p className="mt-2 max-w-2xl text-sm text-slate-500" data-testid="page-description">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Stat({ label, value, sub, testId, accent }) {
  return (
    <div className="card-surface card-hover p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        {accent && <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--descon-blue)" }} />}
      </div>
      <div className="mt-3 font-display text-3xl font-bold tabular-nums text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export function StatusBadge({ status, withDot = true }) {
  const map = {
    active: "text-emerald-700 border-emerald-300 bg-emerald-50",
    completed: "text-emerald-700 border-emerald-300 bg-emerald-50",
    approved: "text-emerald-700 border-emerald-300 bg-emerald-50",
    success: "text-emerald-700 border-emerald-300 bg-emerald-50",
    paid: "text-emerald-700 border-emerald-300 bg-emerald-50",
    present: "text-emerald-700 border-emerald-300 bg-emerald-50",
    assigned: "text-[#1E5CBA] border-[#A8C2EC] bg-[#EFF4FC]",
    in_progress: "text-[#1E5CBA] border-[#A8C2EC] bg-[#EFF4FC]",
    submitted: "text-[#1E5CBA] border-[#A8C2EC] bg-[#EFF4FC]",
    pending_review: "text-[#1E5CBA] border-[#A8C2EC] bg-[#EFF4FC]",
    pending: "text-amber-700 border-amber-300 bg-amber-50",
    expiring_soon: "text-amber-700 border-amber-300 bg-amber-50",
    on_hold: "text-amber-700 border-amber-300 bg-amber-50",
    on_leave: "text-amber-700 border-amber-300 bg-amber-50",
    recorded: "text-slate-700 border-slate-300 bg-slate-50",
    inactive: "text-slate-600 border-slate-300 bg-slate-50",
    archived: "text-slate-600 border-slate-300 bg-slate-100",
    rejected: "text-red-700 border-red-300 bg-red-50",
    overdue: "text-red-700 border-red-300 bg-red-50",
    expired: "text-red-700 border-red-300 bg-red-50",
    suspended: "text-red-700 border-red-300 bg-red-50",
    terminated: "text-red-700 border-red-300 bg-red-50",
    cancelled: "text-red-700 border-red-300 bg-red-50",
    absent: "text-red-700 border-red-300 bg-red-50",
  };
  const dotColor = {
    active: "#10b981", completed: "#10b981", approved: "#10b981", success: "#10b981", paid: "#10b981", present: "#10b981",
    assigned: "#1E5CBA", in_progress: "#1E5CBA", submitted: "#1E5CBA", pending_review: "#1E5CBA",
    pending: "#d97706", expiring_soon: "#d97706", on_hold: "#d97706", on_leave: "#d97706",
    inactive: "#64748b", archived: "#64748b", recorded: "#64748b",
    rejected: "#dc2626", expired: "#dc2626", overdue: "#dc2626", suspended: "#dc2626", terminated: "#dc2626", cancelled: "#dc2626", absent: "#dc2626",
  };
  const cls = map[status] || "text-slate-700 border-slate-300 bg-slate-50";
  const dot = dotColor[status] || "#64748b";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 font-mono-alt text-[10px] font-semibold uppercase tracking-[0.12em] ${cls}`}
      data-testid={`status-${status}`}
    >
      {withDot && <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ background: dot }} />}
      {(status || "—").replace(/_/g, " ")}
    </span>
  );
}

export function EmptyState({ title, description, icon: Icon, action }) {
  return (
    <div className="card-surface px-8 py-16 text-center" data-testid="empty-state">
      {Icon && (
        <div
          className="mx-auto flex h-12 w-12 items-center justify-center rounded-full"
          style={{ background: "var(--descon-blue-soft)", color: "var(--descon-blue)" }}
        >
          <Icon size={22} />
        </div>
      )}
      <div className="mt-4 font-display text-lg font-semibold text-slate-900">{title}</div>
      {description && <div className="mt-1 text-sm text-slate-500">{description}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
